// src/ui/controls/HideExitButton.tsx — P3 D-21 / UI-SPEC §HideExitButton.
//
// Tap-vs-long-press button that lives in the bottom-right HUD cluster when
// self.role === 'hider'. Short-tap (< 150 ms) toggles hide via
// `localInputController.requestToggleHide()`. Long-press (≥ 150 ms) while
// already in status='hiding' engages peek via `setPeekHeld(true)` /
// `setPeekHeld(false)` on release.
//
// WARNING-15 tier-isolation: the only sim-tier import is the controller
// registry singleton — a one-way intent-push seam. No sim-tier types or
// profile data cross into this component.
//
// Source: UI-SPEC §HideExitButton (layout, state table, copywriting,
// tap-vs-long-press flow); PATTERNS.md Table §HUD controls.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { renderBus } from '../../render/bus';
// The controller registry is the ONE sanctioned sim → UI import: a singleton
// that accepts intent-push method calls. No sim-tier data types travel back
// across this seam. This mirrors the TorchButton precedent from P2.
import { localInputController } from '../../sim/controllers/registry';

import './hide-exit-button.css';

/** The local player's entity id. Matches the sim-side convention in tick.ts. */
const PLAYER_ENTITY_ID = 'e0';

/**
 * Perceptual deadband between tap and long-press. The sim's authoritative
 * 250-ms long-press boundary is D-21; the 150-ms deadband here sits inside it
 * so slow users reliably register either intent (UI-SPEC §HideExitButton).
 */
const LONG_PRESS_MS = 150;

type UiStatus = 'free' | 'hiding' | 'peeking' | 'other';

export function HideExitButton(): JSX.Element | null {
  const [role, setRole] = useState<'hunter' | 'hider'>('hider');
  const [status, setStatus] = useState<UiStatus>('free');
  const [candidate, setCandidate] = useState<string | null>(null);

  // Refs track volatile state the pointer handlers need to read without
  // re-running their useCallback dependency graph every tick.
  const pressStartRef = useRef<number | null>(null);
  const peekActiveRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);
  const statusRef = useRef<UiStatus>('free');

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    // narrow: hunter|hider only — dogs/adults never control the hide button.
    const offTick = renderBus.on('sim-tick', (e) => {
      setRole(e.playerRole === 'hunter' ? 'hunter' : 'hider');
      // Map sim-tier statuses we care about to the UI subset; unknown
      // statuses collapse to 'other' so the state machine stays explicit.
      if (e.playerStatus === 'free' || e.playerStatus === 'hiding' || e.playerStatus === 'peeking') {
        setStatus(e.playerStatus);
      } else {
        setStatus('other');
      }
    });
    const offHidden = renderBus.on('hiding-entered', (e) => {
      if (e.entityId === PLAYER_ENTITY_ID) setStatus('hiding');
    });
    const offExited = renderBus.on('hiding-exited', (e) => {
      if (e.entityId === PLAYER_ENTITY_ID) setStatus('free');
    });
    const offPeekStart = renderBus.on('peek-started', (e) => {
      if (e.entityId === PLAYER_ENTITY_ID) setStatus('peeking');
    });
    const offPeekEnd = renderBus.on('peek-ended', (e) => {
      if (e.entityId === PLAYER_ENTITY_ID) setStatus('hiding');
    });
    const offCand = renderBus.on('hide-candidate-changed', (e) => {
      if (e.entityId === PLAYER_ENTITY_ID) setCandidate(e.spotId);
    });
    return () => {
      offTick();
      offHidden();
      offExited();
      offPeekStart();
      offPeekEnd();
      offCand();
    };
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      pressStartRef.current = performance.now();
      longPressTimerRef.current = window.setTimeout(() => {
        // Only engage peek when already hiding — mirror of UI-SPEC contract.
        if (statusRef.current === 'hiding') {
          localInputController.setPeekHeld(true);
          peekActiveRef.current = true;
        }
      }, LONG_PRESS_MS);
    },
    [],
  );

  const onPointerUpOrCancel = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      if (peekActiveRef.current) {
        localInputController.setPeekHeld(false);
        peekActiveRef.current = false;
        pressStartRef.current = null;
        return;
      }
      const start = pressStartRef.current;
      pressStartRef.current = null;
      if (start === null) return;
      const dur = performance.now() - start;
      if (dur < LONG_PRESS_MS) {
        localInputController.requestToggleHide();
      }
    },
    [],
  );

  if (role !== 'hider') return null;

  const disabled = status === 'free' && candidate === null;
  const dataState =
    status === 'hiding'
      ? 'hiding'
      : status === 'peeking'
        ? 'peeking'
        : disabled
          ? 'greyed'
          : 'armed';
  const ariaLabel = disabled
    ? 'Hide (no spot in range)'
    : status === 'hiding'
      ? 'Exit hiding spot'
      : status === 'peeking'
        ? 'Release peek'
        : 'Hide here';

  return (
    <button
      type="button"
      className="hide-exit-button"
      data-testid="hide-exit-button"
      data-state={dataState}
      data-disabled={disabled ? 'true' : 'false'}
      aria-label={ariaLabel}
      aria-pressed={status === 'hiding' || status === 'peeking'}
      onPointerDown={disabled ? undefined : onPointerDown}
      onPointerUp={disabled ? undefined : onPointerUpOrCancel}
      onPointerCancel={disabled ? undefined : onPointerUpOrCancel}
      // Inline style mirrors hide-exit-button.css (PATTERNS Pattern E —
      // same convention as TorchButton so jsdom tests can assert layout
      // shape without loading the stylesheet).
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom) + 16px + 48px + 12px)',
        right: 'calc(env(safe-area-inset-right) + 16px + 4px)',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        touchAction: 'none',
        pointerEvents: disabled ? 'none' : 'auto',
        zIndex: 60,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Inline pictograph SVG — simple bush/tree silhouette (stroke-only
       *  when armed, filled in the on states via CSS). P6 may replace. */}
      <svg
        viewBox="0 0 32 32"
        width={32}
        height={32}
        aria-hidden="true"
        focusable="false"
      >
        <circle
          cx={16}
          cy={16}
          r={10}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        />
      </svg>
    </button>
  );
}
